import React, { forwardRef, useContext } from 'react';
import { IDefaultLinkProps, TLinkTarget } from './models';
import { AppContext } from '@src/providers/App/AppContext';
import { IDefaultComponentProps, ILinkComponent, ITextDecorationProps } from '@src/models/props';
import { IRequiredIdentify } from '@src/models/analytics';
import { classnames } from '@src/utils/classnames';
import { getClasses } from '@src/utils/getClass';
import styled from './Link.scss';
import { onUnitClickCallback } from '@src/core/Unit';

interface IPropsBase
  extends IDefaultComponentProps,
    IRequiredIdentify,
    IDefaultLinkProps,
    ITextDecorationProps,
    React.AnchorHTMLAttributes<HTMLAnchorElement> {
  id: string;

  /** On click handler. */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;

  /** Значение атрибута target */
  target?: TLinkTarget;

  /** Использовать ссылку без дефолтных стилей */
  noStyles?: boolean;
}

/** Компонент ссылки */
const Component = forwardRef<HTMLAnchorElement, IPropsBase>(
  ({ id, children, noStyles, textDecoration = '', className, ...props }, ref) => {
    const classes = classnames(className, styled.root, ...getClasses(styled, { textDecoration }), {
      [styled.withStyles]: !noStyles,
    });

    return (
      <a data-id={id} className={classes} {...props} onClick={onUnitClickCallback(id, props.onClick)} ref={ref}>
        {children}
      </a>
    );
  },
);

export interface ILinkProps extends IPropsBase, ILinkComponent {}

/** Компонент ссылки, принимает кастомную обертку при необходимости для использования в проектах nextjs */
export const Link = forwardRef<HTMLAnchorElement, ILinkProps>(({ disableNextJs, ...props }, ref) => {
  const { nextComponent } = useContext(AppContext);

  if (!disableNextJs && nextComponent?.Link && props.href) {
    return (
      <nextComponent.Link href={props.href} passHref={true} legacyBehavior>
        <Component {...props} ref={ref} />
      </nextComponent.Link>
    );
  }

  return <Component {...props} ref={ref} />;
});

Link.displayName = 'Link';
